import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Briefcase, Plus, Trash2, Eye, FileText, Award, Send } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LDL = () => {
  const { canManageLDL, hasPermesso, isSindacatoAdmin } = useAuth();
  const canManage = canManageLDL();
  const canRichiediCurriculum = () => isSindacatoAdmin() || hasPermesso('richiedi_curriculum_municipio') || hasPermesso('gestisci_ldl');
  const [ldls, setLdls] = useState([]);
  const [curriculums, setCurriculums] = useState([]);
  const [concorsi, setConcorsi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLDL, setSelectedLDL] = useState(null);
  const [formData, setFormData] = useState({
    curriculum_id: '',
    concorso_id: '',
    lettera_motivazionale: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ldlRes, cvRes, concorsiRes] = await Promise.all([
        axios.get(`${API_URL}/ldl`),
        axios.get(`${API_URL}/curriculum`),
        axios.get(`${API_URL}/concorsi`)
      ]);
      setLdls(ldlRes.data);
      setCurriculums(cvRes.data);
      setConcorsi(concorsiRes.data.filter(c => c.stato === 'APERTO'));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ldl`, formData);
      toast.success('Domanda di lavoro inviata');
      setIsDialogOpen(false);
      setFormData({ curriculum_id: '', concorso_id: '', lettera_motivazionale: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    }
  };

  const handleStatusChange = async (id, stato) => {
    try {
      await axios.patch(`${API_URL}/ldl/${id}/stato?stato=${stato}`);
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa domanda?')) return;
    try {
      await axios.delete(`${API_URL}/ldl/${id}`);
      toast.success('Domanda eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleRichiediCurriculum = async (ldl) => {
    try {
      // Trova l'user_id dal curriculum
      const curriculum = curriculums.find(c => c.id === ldl.curriculum_id);
      const userId = curriculum?.user_id || ldl.user_id;
      
      if (!userId) {
        toast.error('Utente non trovato per questo curriculum');
        return;
      }
      
      await axios.post(`${API_URL}/ldl/richiedi-curriculum`, {
        user_id: userId,
        note: `Richiesta per domanda: ${ldl.titolo_concorso}`
      });
      toast.success('Richiesta curriculum inviata al Municipio');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella richiesta');
    }
  };

  const getStatusBadge = (stato) => {
    const variants = {
      'INVIATA': 'bg-blue-100 text-blue-800',
      'IN_REVISIONE': 'bg-amber-100 text-amber-800',
      'ACCETTATA': 'bg-green-100 text-green-800',
      'RIFIUTATA': 'bg-red-100 text-red-800'
    };
    const labels = {
      'INVIATA': 'Inviata',
      'IN_REVISIONE': 'In Revisione',
      'ACCETTATA': 'Accettata',
      'RIFIUTATA': 'Rifiutata'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${variants[stato]}`}>
        {labels[stato]}
      </span>
    );
  };

  return (
    <div data-testid="ldl-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Briefcase className="w-8 h-8" />
            Domande di Lavoro
          </h1>
          <p className="text-slate-600 mt-1">Gestione candidature ai concorsi</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-ldl-btn" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Domanda
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Invia Domanda di Lavoro</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Seleziona Curriculum</Label>
                <Select
                  value={formData.curriculum_id}
                  onValueChange={(value) => setFormData({ ...formData, curriculum_id: value })}
                >
                  <SelectTrigger data-testid="ldl-cv-select">
                    <SelectValue placeholder="Scegli un curriculum" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="z-[100]">
                    {curriculums.map((cv) => (
                      <SelectItem key={cv.id} value={cv.id}>
                        {cv.nome_candidato} {cv.cognome_candidato}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Seleziona Concorso</Label>
                <Select
                  value={formData.concorso_id}
                  onValueChange={(value) => setFormData({ ...formData, concorso_id: value })}
                >
                  <SelectTrigger data-testid="ldl-concorso-select">
                    <SelectValue placeholder="Scegli un concorso aperto" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="z-[100]">
                    {concorsi.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.titolo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lettera">Lettera Motivazionale</Label>
                <Textarea
                  id="lettera"
                  data-testid="ldl-lettera-input"
                  value={formData.lettera_motivazionale}
                  onChange={(e) => setFormData({ ...formData, lettera_motivazionale: e.target.value })}
                  placeholder="Scrivi la lettera motivazionale..."
                  rows={6}
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  data-testid="submit-ldl-btn" 
                  className="bg-slate-900 hover:bg-slate-800"
                  disabled={!formData.curriculum_id || !formData.concorso_id}
                >
                  Invia Domanda
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* LDL Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : ldls.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna domanda di lavoro</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Candidato</TableHead>
                <TableHead>Concorso</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ldls.map((ldl) => (
                <TableRow key={ldl.id} data-testid={`ldl-row-${ldl.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      {ldl.nome_candidato} {ldl.cognome_candidato}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-amber-600" />
                      {ldl.titolo_concorso}
                    </div>
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                      <Select
                        value={ldl.stato}
                        onValueChange={(value) => handleStatusChange(ldl.id, value)}
                      >
                        <SelectTrigger className="w-36" data-testid={`ldl-status-${ldl.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INVIATA">Inviata</SelectItem>
                          <SelectItem value="IN_REVISIONE">In Revisione</SelectItem>
                          <SelectItem value="ACCETTATA">Accettata</SelectItem>
                          <SelectItem value="RIFIUTATA">Rifiutata</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      getStatusBadge(ldl.stato)
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {new Date(ldl.created_at).toLocaleDateString('it-IT')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`view-ldl-${ldl.id}`}
                        onClick={() => { setSelectedLDL(ldl); setViewDialogOpen(true); }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canRichiediCurriculum() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`richiedi-cv-${ldl.id}`}
                          onClick={() => handleRichiediCurriculum(ldl)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Richiedi Curriculum al Municipio"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-ldl-${ldl.id}`}
                          onClick={() => handleDelete(ldl.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* View LDL Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Dettaglio Domanda</DialogTitle>
          </DialogHeader>
          {selectedLDL && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Candidato</Label>
                  <p className="font-medium">{selectedLDL.nome_candidato} {selectedLDL.cognome_candidato}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Concorso</Label>
                  <p className="font-medium">{selectedLDL.titolo_concorso}</p>
                </div>
              </div>
              <div>
                <Label className="text-slate-500">Lettera Motivazionale</Label>
                <p className="mt-1 whitespace-pre-wrap bg-slate-50 p-4 rounded-sm">{selectedLDL.lettera_motivazionale}</p>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Stato: {getStatusBadge(selectedLDL.stato)}</span>
                  <span className="text-sm text-slate-500">
                    Inviata il {new Date(selectedLDL.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LDL;
